import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Heart, Phone, X, Facebook, Instagram, Linkedin } from "lucide-react";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useFavorites } from "@/hooks/useFavorites";

const wording = {
  fr: { open: "Ouvrir le menu", close: "Fermer le menu", closeWord: "Fermer", language: "Langue", favorites: "Ma sélection", menu: "Menu" },
  es: { open: "Abrir menú", close: "Cerrar menú", closeWord: "Cerrar", language: "Idioma", favorites: "Mi selección", menu: "Menú" },
  en: { open: "Open menu", close: "Close menu", closeWord: "Close", language: "Language", favorites: "My collection", menu: "Menu" },
};

const social = [
  { href: "https://www.facebook.com/share/1Znv4Qgm5Z/?mibextid=wwXIfr", label: "Facebook", icon: Facebook },
  { href: "https://www.instagram.com/dounia_elmoutii_properties_?igsh=dWtudnlreHlqYXdh", label: "Instagram", icon: Instagram },
  { href: "https://www.linkedin.com/in/dounia-el-moutii-0423b79b/", label: "LinkedIn", icon: Linkedin },
] as const;

const languages = ["en", "es", "fr"] as const;

export default function Header() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { favorites } = useFavorites();
  const { scrollY, scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  const reducedMotion = useReducedMotion();
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 24);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const language = i18n.language.startsWith("fr") ? "fr" : i18n.language.startsWith("es") ? "es" : "en";
  const labels = wording[language];
  const isAtTop = !isScrolled;
  const hasDarkHero =
    pathname === "/" ||
    pathname === "/properties" ||
    pathname === "/about" ||
    pathname === "/contact" ||
    pathname === "/privacy-policy" ||
    pathname === "/legal-notice" ||
    pathname === "/terms-of-service";
  const ease = [0.22, 1, 0.36, 1] as const;
  const menuItems = [
    { label: t("nav.home", "Home"), href: "/" },
    { label: t("nav.properties", "Properties"), href: "/properties" },
    { label: t("nav.about", "About"), href: "/about" },
    { label: t("nav.contact", "Contact"), href: "/contact" },
  ];

  useMotionValueEvent(scrollY, "change", latest => setIsScrolled(latest > 24));

  useEffect(() => {
    if (!isMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    const root = document.getElementById("root");
    const previousInert = root?.inert ?? false;
    if (root) root.inert = true;
    document.body.style.overflow = "hidden";
    const frame = requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLButtonElement>(".dem-menu-close")?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
      if (event.key !== "Tab") return;
      const elements = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
      if (!elements?.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      if (root) root.inert = previousInert;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      triggerRef.current?.focus();
    };
  }, [isMenuOpen]);

  return <>
    <header
      className={`dem-header ${
        isAtTop ? "dem-header-at-top" : "dem-header-on-page"
      } ${isAtTop && hasDarkHero ? "dem-header-on-hero" : ""}`}
    >
      <motion.div
        className="pointer-events-none fixed left-0 right-0 top-0 z-[70] h-0.5 origin-left bg-[#a26850]"
        style={{ scaleX }}
        aria-hidden="true"
      />
      <div className="dem-header-inner">
        <Link to="/" className="dem-brand" aria-label="DEM Properties">
          <img src="/logo.webp" alt="DEM Properties" width={841} height={363} />
        </Link>
        <div className="dem-header-tools">
          <div className="dem-header-langs" role="group" aria-label={labels.language}>
            {languages.map(lang => (
              <button key={lang} type="button" aria-pressed={language === lang} onClick={() => void i18n.changeLanguage(lang)}>
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          <Link to="/favorites" className="dem-favorites" aria-label={`${labels.favorites}${favorites.length ? ` (${favorites.length})` : ""}`}>
            <Heart size={18} strokeWidth={1.35} />
            {favorites.length > 0 && <span>{favorites.length}</span>}
          </Link>
          <button ref={triggerRef} type="button" className="dem-menu-trigger" aria-label={labels.open} aria-haspopup="dialog" aria-expanded={isMenuOpen} aria-controls="dem-menu" onClick={() => setIsMenuOpen(true)}>
            <span className="dem-menu-word">{labels.menu}</span>
            <span className="dem-menu-bars" aria-hidden="true"><i /><i /></span>
          </button>
        </div>
      </div>
    </header>
    {createPortal(<AnimatePresence>
      {isMenuOpen && (
        <motion.div
          key="menu"
          ref={dialogRef}
          id="dem-menu"
          role="dialog"
          aria-modal="true"
          aria-label={labels.menu}
          className="dem-menu"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.45, ease }}
        >
          <div className="dem-menu-top">
            <div className="dem-menu-langs" role="group" aria-label={labels.language}>
              {languages.map(lang => (
                <button key={lang} type="button" aria-pressed={language === lang} onClick={() => void i18n.changeLanguage(lang)}>
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <button type="button" className="dem-menu-close" aria-label={labels.close} onClick={() => setIsMenuOpen(false)}>
              <span>{labels.closeWord}</span>
              <X size={22} strokeWidth={1.25} />
            </button>
          </div>
          <div className="dem-menu-center">
            <nav className="dem-menu-links" aria-label={labels.menu}>
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, y: reducedMotion ? 0 : 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reducedMotion ? 0 : 0.5, delay: reducedMotion ? 0 : 0.08 + index * 0.06, ease }}
                >
                  <Link to={item.href} onClick={() => setIsMenuOpen(false)} aria-current={pathname === item.href ? "page" : undefined}>
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <a className="dem-menu-phone" href="tel:+34655623860">
              <Phone size={16} strokeWidth={1.5} aria-hidden="true" />
              +34 655 62 38 60
            </a>
          </div>
          <div className="dem-menu-foot">
            <nav aria-label={t("footer.menu.title")}>
              <Link to="/legal-notice" onClick={() => setIsMenuOpen(false)}>{t("footer.legal")}</Link>
              <Link to="/terms-of-service" onClick={() => setIsMenuOpen(false)}>{t("footer.terms")}</Link>
              <Link to="/privacy-policy" onClick={() => setIsMenuOpen(false)}>{t("footer.privacy")}</Link>
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  window.dispatchEvent(new Event("open-cookie-settings"));
                }}
              >
                {t("footer.cookies")}
              </button>
            </nav>
            <div className="dem-menu-socials">
              {social.map(({ href, label, icon: Icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                  <Icon size={16} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>, document.body)}
  </>;
}
